
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * CommissionValue struct
 */
class CommissionValue {
  /**
   * Initialize struct
   * @param {!number=} from
   * @param {!number=} to
   * @param {!number=} commission
   * @param {!number=} minCommission
   * @param {!number=} maxCommission
   * @constructor
   */
  constructor (from = 0.0, to = 0.0, commission = 0.0, minCommission = 0.0, maxCommission = 0.0) {
    this.From = from
    this.To = to
    this.Commission = commission
    this.MinCommission = minCommission
    this.MaxCommission = maxCommission
  }

  /**
   * Copy struct (shallow copy)
   * @this {!CommissionValue}
   * @param {!CommissionValue} other Other struct
   * @returns {!CommissionValue} This struct
   */
  copy (other) {
    if (other.From != null) {
      this.From = other.From
    } else {
      this.From = null
    }
    if (other.To != null) {
      this.To = other.To
    } else {
      this.To = null
    }
    if (other.Commission != null) {
      this.Commission = other.Commission
    } else {
      this.Commission = null
    }
    if (other.MinCommission != null) {
      this.MinCommission = other.MinCommission
    } else {
      this.MinCommission = null
    }
    if (other.MaxCommission != null) {
      this.MaxCommission = other.MaxCommission
    } else {
      this.MaxCommission = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!CommissionValue}
   * @returns {!CommissionValue} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new CommissionValueModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new CommissionValueModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!CommissionValue}
   * @param {!CommissionValue} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof CommissionValue)) {
      throw new TypeError('Instance of CommissionValue is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!CommissionValue}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      From: ((this.From != null) ? this.From : null),
      To: ((this.To != null) ? this.To : null),
      Commission: ((this.Commission != null) ? this.Commission : null),
      MinCommission: ((this.MinCommission != null) ? this.MinCommission : null),
      MaxCommission: ((this.MaxCommission != null) ? this.MaxCommission : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return CommissionValue.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!CommissionValue} other Object value
   * @returns {!CommissionValue} Created struct
   */
  static fromObject (other) {
    return new CommissionValue().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!CommissionValue}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return CommissionValue.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!CommissionValue}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 20
  }
}

export { CommissionValue };

/**
 * Fast Binary Encoding CommissionValue field model
 */
class FieldModelCommissionValue extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.From = new fbe.FieldModelDouble(buffer, 4 + 4)
    this.To = new fbe.FieldModelDouble(buffer, this.From.fbeOffset + this.From.fbeSize)
    this.Commission = new fbe.FieldModelDouble(buffer, this.To.fbeOffset + this.To.fbeSize)
    this.MinCommission = new fbe.FieldModelDouble(buffer, this.Commission.fbeOffset + this.Commission.fbeSize)
    this.MaxCommission = new fbe.FieldModelDouble(buffer, this.MinCommission.fbeOffset + this.MinCommission.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelCommissionValue}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelCommissionValue}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.From.fbeSize + this.To.fbeSize + this.Commission.fbeSize + this.MinCommission.fbeSize + this.MaxCommission.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelCommissionValue}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.From.fbeExtra + this.To.fbeExtra + this.Commission.fbeExtra + this.MinCommission.fbeExtra + this.MaxCommission.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelCommissionValue}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelCommissionValue.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelCommissionValue}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 20
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelCommissionValue}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelCommissionValue}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.From.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.From.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.From.fbeSize

    if ((fbeCurrentSize + this.To.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.To.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.To.fbeSize

    if ((fbeCurrentSize + this.Commission.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Commission.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Commission.fbeSize

    if ((fbeCurrentSize + this.MinCommission.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MinCommission.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MinCommission.fbeSize

    if ((fbeCurrentSize + this.MaxCommission.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MaxCommission.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MaxCommission.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelCommissionValue}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelCommissionValue}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelCommissionValue}
   * @param {!CommissionValue} fbeValue Default value, defaults is new CommissionValue()
   * @returns {!CommissionValue} CommissionValue value
   */
  get (fbeValue = new CommissionValue()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelCommissionValue}
   * @param {!CommissionValue} fbeValue CommissionValue value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.From.fbeSize) <= fbeStructSize) {
      fbeValue.From = this.From.get()
    } else {
      fbeValue.From = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.From.fbeSize

    if ((fbeCurrentSize + this.To.fbeSize) <= fbeStructSize) {
      fbeValue.To = this.To.get()
    } else {
      fbeValue.To = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.To.fbeSize

    if ((fbeCurrentSize + this.Commission.fbeSize) <= fbeStructSize) {
      fbeValue.Commission = this.Commission.get()
    } else {
      fbeValue.Commission = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Commission.fbeSize

    if ((fbeCurrentSize + this.MinCommission.fbeSize) <= fbeStructSize) {
      fbeValue.MinCommission = this.MinCommission.get(0.0)
    } else {
      fbeValue.MinCommission = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MinCommission.fbeSize

    if ((fbeCurrentSize + this.MaxCommission.fbeSize) <= fbeStructSize) {
      fbeValue.MaxCommission = this.MaxCommission.get(0.0)
    } else {
      fbeValue.MaxCommission = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MaxCommission.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelCommissionValue}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelCommissionValue}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelCommissionValue}
   * @param {!CommissionValue} fbeValue CommissionValue value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelCommissionValue}
   * @param {CommissionValue} fbeValue CommissionValue value
   */
  setFields (fbeValue) {
    this.From.set(fbeValue.From)
    this.To.set(fbeValue.To)
    this.Commission.set(fbeValue.Commission)
    this.MinCommission.set(fbeValue.MinCommission)
    this.MaxCommission.set(fbeValue.MaxCommission)
  }
}

export { FieldModelCommissionValue };

/**
 * Fast Binary Encoding CommissionValue model
 */
class CommissionValueModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelCommissionValue(this.buffer, 4)
  }

  /**
   * Get the CommissionValue model
   * @this {!CommissionValueModel}
   * @returns {!FieldModelCommissionValue} model CommissionValue model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!CommissionValueModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!CommissionValueModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return CommissionValueModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!CommissionValueModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelCommissionValue.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!CommissionValueModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!CommissionValueModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!CommissionValueModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!CommissionValueModel}
   * @param {!CommissionValue} value CommissionValue value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!CommissionValueModel}
   * @param {!CommissionValue} value CommissionValue value, defaults is new CommissionValue()
   * @return {!object} Deserialized CommissionValue value and its size
   */
  deserialize (value = new CommissionValue()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new CommissionValue(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new CommissionValue(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!CommissionValueModel}
   * @param {!number} prev Previous CommissionValue model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { CommissionValueModel };
